import {
  isSupabaseConfigured,
  supabase,
  supabaseConfigurationError,
} from '../../supabase'
import type {
  AuthSession,
  AuthUser,
  DatabaseProbe,
  ProfileRow,
  RepositoryResult,
} from '../types/database.types'
import { failure, success } from './result'

export const AdminRepository = {
  isConfigured(): boolean {
    return isSupabaseConfigured
  },

  configurationError(): string {
    return supabaseConfigurationError
  },

  async signIn(
    email: string,
    password: string,
  ): Promise<RepositoryResult<AuthSession>> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error || !data.session || !data.user) {
      return failure(error ?? new Error('No fue posible iniciar sesión.'))
    }

    return success({
      accessToken: data.session.access_token,
      user: { id: data.user.id, email: data.user.email ?? null },
    })
  },

  async signOut(): Promise<RepositoryResult<null>> {
    const { error } = await supabase.auth.signOut()
    return error ? failure(error) : success(null)
  },

  async getSession(): Promise<RepositoryResult<AuthSession | null>> {
    const { data, error } = await supabase.auth.getSession()
    if (error) return failure(error)

    return success(
      data.session?.user
        ? {
            accessToken: data.session.access_token,
            user: {
              id: data.session.user.id,
              email: data.session.user.email ?? null,
            },
          }
        : null,
    )
  },

  async getProfile(
    userId: string,
  ): Promise<RepositoryResult<ProfileRow | null>> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email, role, active, avatar_url')
      .eq('id', userId)
      .maybeSingle()

    return error ? failure(error) : success((data ?? null) as ProfileRow | null)
  },

  subscribeToAuth(callback: () => void): () => void {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => callback())

    return () => subscription.unsubscribe()
  },

  async probeTables(tables: readonly string[]): Promise<DatabaseProbe[]> {
    return Promise.all(
      tables.map(async (table) => {
        const { count, error } = await supabase
          .from(table)
          .select('*', { count: 'exact', head: true })

        return {
          table,
          count: error ? null : count,
          errorCode: error?.code,
        }
      }),
    )
  },

  async getCurrentUser(): Promise<RepositoryResult<AuthUser | null>> {
    const { data, error } = await supabase.auth.getUser()
    return error
      ? failure(error)
      : success(
          data.user
            ? { id: data.user.id, email: data.user.email ?? null }
            : null,
        )
  },
}
